package com.snakesworld.game.domain

import com.snakesworld.game.engine.GameConstants
import com.snakesworld.game.engine.SnakeSegment
import com.snakesworld.game.engine.Vector2D
import com.snakesworld.game.network.PelletNetworkState

/**
 * Converts deceased snake body cells into collectible death orbs.
 * Each orb is a uniform 1-mass grid square (same size as live snake parts),
 * snapped onto the [GameConstants.SEGMENT_SPACING] grid. Mass/length do not
 * change orb size.
 */
object DeathDropCalculator {

    fun generateDeathOrbs(
        victimId: String,
        headPosition: Vector2D,
        segments: List<SnakeSegment>,
    ): List<PelletNetworkState> {
        val allPoints = ArrayList<Vector2D>(segments.size + 1).apply {
            add(headPosition)
            addAll(segments.map { it.position })
        }

        if (allPoints.isEmpty()) return emptyList()

        // Subsample the corpse trail so Firestore is not flooded; skip duplicate cells.
        val step = if (allPoints.size > 15) 3 else 2
        val occupiedCells = HashSet<Pair<Int, Int>>()
        val orbs = ArrayList<PelletNetworkState>()
        val colors = listOf(
            GameConstants.COLOR_NEON_YELLOW,
            GameConstants.COLOR_NEON_MAGENTA,
            GameConstants.COLOR_NEON_CYAN,
            GameConstants.COLOR_NEON_GREEN,
        )
        val spacing = GameConstants.SEGMENT_SPACING

        for (i in allPoints.indices step step) {
            // A head that stepped through the wall sits on the first grid center
            // outside the arena. Pull that orb back so the square stays inside.
            val snapped = CollisionDetection.clampPelletInsideArena(allPoints[i])
            val cell = Pair(
                roundToCellIndex(snapped.x, spacing),
                roundToCellIndex(snapped.y, spacing),
            )
            if (!occupiedCells.add(cell)) continue

            orbs.add(
                PelletNetworkState(
                    id = "death_${victimId}_${orbs.size}",
                    x = (snapped.x * 10f).toInt() / 10f,
                    y = (snapped.y * 10f).toInt() / 10f,
                    m = 1f,
                    c = colors[orbs.size % colors.size],
                    cp = true,
                    bt = false,
                )
            )
        }
        return orbs
    }

    private fun roundToCellIndex(value: Float, spacing: Float): Int {
        return CollisionDetection.snapToGrid(value / spacing, 1f).toInt()
    }
}
